/**
 * @file DocumentHistoryPanel.tsx
 * @description Блок «Движения / история по документу» (phase19.23 этап A).
 *
 * Домен-нейтральный: принимает готовые строки, поэтому одинаково годится для
 * движений склада по списанию, истории стадий заказа и платежей по счёту.
 * Загрузка данных — забота вызывающего адаптера.
 *
 * В строку раскладывается с `lg`, как таблица позиций: на 768px четыре
 * колонки просили ≈588px при доступных ≈464px, и автор движения обрезался до
 * «Ад» (замер ui-visual-qa 2026-09-20). Колонки не `shrink-0`, а `min-w-0` с
 * `truncate`: длинное ФИО ужимается многоточием, а не режется рамкой.
 *
 * ```tsx
 * <DocumentHistoryPanel
 *   title="Движения по документу"
 *   rows={rows}
 *   loading={loading}
 *   emptyText="Движений по документу нет."
 * />
 * ```
 */

import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import { Alert } from "./alert";
import { SectionLabel } from "./SectionLabel";

/** Одна запись истории: когда, что, по чему и на сколько. */
export interface DocumentHistoryRow {
  id: string;
  /** Дата и время — уже отформатированные под локаль. */
  when: string;
  /** Тип операции: «Списание», «Смена стадии». */
  kind: string;
  /** Предмет операции: товар, партия, поле. */
  title: ReactNode;
  /** Вторая строка: артикул, партия, комментарий. */
  subtitle?: ReactNode;
  /** Количество/сумма со знаком — красим по знаку. */
  amount?: string | null;
  /** Кто сделал. */
  actor?: string | null;
}

interface Props {
  title: string;
  rows: DocumentHistoryRow[];
  loading?: boolean;
  error?: string | null;
  emptyText: string;
  /** Сноска под списком: «показаны не все записи» и подобное. */
  note?: string | null;
}

export function DocumentHistoryPanel({
  title,
  rows,
  loading = false,
  error = null,
  emptyText,
  note = null,
}: Props) {
  return (
    <section className="flex flex-col gap-2">
      {/* SC43: подписи капсом не используются — общая подпись секции вместо ручного капса. */}
      <SectionLabel count={!loading && !error ? rows.length : undefined}>{title}</SectionLabel>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-on-surface/15 bg-surface-lowest px-4 py-6 text-center text-sm text-on-surface-variant">
          {emptyText}
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-on-surface/8 overflow-hidden rounded-xl border border-on-surface/8 bg-surface-lowest">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-col gap-1 px-3 py-2.5 lg:flex-row lg:items-center lg:gap-3"
            >
              <span className="shrink-0 font-mono text-xs tabular-nums text-on-surface-variant lg:w-36">
                {row.when}
              </span>
              <span className="min-w-0 truncate text-sm lg:w-32">{row.kind}</span>
              <span className="min-w-0 flex-1 text-sm">
                <span className="block truncate font-medium">{row.title}</span>
                {row.subtitle ? (
                  <span className="block truncate text-xs text-on-surface-variant">
                    {row.subtitle}
                  </span>
                ) : null}
              </span>
              {row.amount ? <AmountCell amount={row.amount} /> : null}
              {row.actor ? (
                <span className="min-w-0 truncate text-xs text-on-surface-variant lg:w-40 lg:text-right">
                  {row.actor}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {!loading && !error && note ? (
        <p className="text-xs text-on-surface-variant">{note}</p>
      ) : null}
    </section>
  );
}

/**
 * Приход зелёным, расход красным — знак читается быстрее слова. Ноль и
 * нечисловое значение остаются нейтральными: зелёный «0» читается как приход,
 * которого не было.
 */
function AmountCell({ amount }: { amount: string }) {
  const value = Number(amount);
  const known = Number.isFinite(value);
  return (
    <span
      className={cn(
        "shrink-0 font-mono text-sm tabular-nums sm:w-24 sm:text-right",
        !known || value === 0
          ? "text-on-surface-variant"
          : value < 0
            ? "text-red-600"
            : "text-emerald-600",
      )}
    >
      {known ? `${value > 0 ? "+" : ""}${value.toLocaleString("ru-RU")}` : amount}
    </span>
  );
}
